import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Container } from "@roqua/components/container";
import { Section, SectionHeader } from "@roqua/components/sections";

import { BackButton } from "@/components/BackButton";
import { graphql } from "@/gql";
import { useVerifiedSession } from "@/session";
import { ErrorPage } from "@/components/ErrorPage";
import { toError } from "@/lib/utils";

const CompletedRecentResponsesForCurrentRespondentWithAnswers = graphql(
  /* GraphQL */ `
    query completedRecentResponsesForCurrentRespondentWithAnswers(
      $respondentId: ID!
      $responseId: ID!
    ) {
      respondent(id: $respondentId) {
        id
        response(id: $responseId) {
          id
          completedAt
          questionnaire {
            name
          }
          respondent {
            type
          }
          answers {
            id
            __typename
            isAnswered
            question {
              contextFreeTitle
            }
            ... on SingleSelectAnswer {
              selectedOption {
                key
                value
                description
              }
            }
            ... on MultiSelectAnswer {
              selectedOptions {
                key
                value
                description
              }
            }
            ... on TextAnswer {
              answerText
            }
            ... on IntegerAnswer {
              answerText
            }
            ... on FloatAnswer {
              answerText
            }
            ... on DatePartsAnswer {
              answerText
            }
          }
        }
      }
    }
  `,
);

export const Route = createFileRoute("/portal/outcomes/$responseId/answers")({
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorPage message={toError(error).message} />
});

function RouteComponent() {
  const { responseId } = Route.useParams();
  const { client, user } = useVerifiedSession();
  const {
    data: { respondent },
  } = useSuspenseQuery({
    queryKey: ["OutcomeAnswers", responseId],
    queryFn: () =>
      client.request(CompletedRecentResponsesForCurrentRespondentWithAnswers, {
        respondentId: user.id,
        responseId: responseId,
      }),
  });

  const currentResponse = respondent.response;

  return (
    <>
      <Section variant="header">
        <SectionHeader
          header={currentResponse.questionnaire.name}
          buttons={<BackButton />}
        />
      </Section>
      <Section variant="content">
        <Container>
          {currentResponse.answers.map((answer, index) => (
            <>
              {answer.isAnswered && (
                <div
                  key={answer.id}
                  className={
                    index % 2 === 1
                      ? "flex justify-between bg-gray-200 p-2"
                      : "flex justify-between p-2"
                  }
                >
                  <p className="w-2/3">{answer.question.contextFreeTitle}</p>
                  {answer.__typename === "SingleSelectAnswer" && (
                    <p className="w-1/4 text-right">
                      Keuze {answer.selectedOption?.value} (
                      {answer.selectedOption?.description})
                    </p>
                  )}
                  {answer.__typename === "TextAnswer" && (
                    <p className="w-1/4 text-right">{answer.answerText}</p>
                  )}
                  {answer.__typename === "MultiSelectAnswer" && (
                    <div className="flex flex-col">
                      {answer.selectedOptions.map((option) => (
                        <p key={option.key} className="w-1/4 text-right">
                          Keuze {option.value} ({option.description})
                        </p>
                      ))}
                    </div>
                  )}
                  {"answerText" in answer && (
                    <p className="w-1/4 text-right">{answer.answerText}</p>
                  )}
                </div>
              )}
            </>
          ))}
        </Container>
      </Section>
    </>
  );
}
